import React, { MutableRefObject } from 'react';
import { IChunk, INote } from '../../global/interfaces';
import { IBooking, IDelta, IPromptBlock } from '@homeporter/types';
import { BOOKING_STATUSES_DISPLAY } from '../data';
import EXIF from 'exif-js';

import {
  ITranscriptPart,
  ITranscript,
  IImage,
  ITranscriptSegment,
  ITranscriptSentence,
} from '../../global/interfaces';

export const storeItem = (key: string, data: any) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.log('Something went wrong', error);
  }
};

export const retrieveItem = (key: string) => {
  try {
    const data = window.localStorage.getItem(key);
    if (data) return JSON.parse(data);
    return null;
  } catch (error) {
    console.log('Something went wrong', error);
    return null;
  }
};

export const removeItem = (key: string) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.log('Something went wrong', error);
  }
};

export const generateRandomId = () => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';

  for (let i = 0; i < 12; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    id += characters.charAt(randomIndex);
  }

  return id;
};

export const fillIn = (image: any, canvas: any, zoom: number) => {
  const sx = Number(canvas.width * zoom) / Number(image.videoWidth);
  const sy = Number(canvas.height * zoom) / Number(image.videoHeight);
  var s = sx > sy ? sx : sy;

  const area: any = {
    width: image.videoWidth * s,
    height: image.videoHeight * s,
  };
  area.left = (canvas.width - area.width) / 2;
  area.top = (canvas.height - area.height) / 2;

  return area;
};

export const handleEnableMicVisualizer = (
  volumeCallbackRef: any,
  volumeIntervalRef: any,
) => {
  if (
    volumeCallbackRef.current !== null &&
    volumeIntervalRef.current === null
  ) {
    volumeIntervalRef.current = setInterval(volumeCallbackRef.current, 0);
  }
};

export const handleDisableMicVisualizer = (volumeIntervalRef: any) => {
  if (volumeIntervalRef.current !== null) {
    clearInterval(volumeIntervalRef.current);
    volumeIntervalRef.current = null;
  }
};

export const convertToBase64 = async (image_url: string) => {
  try {
    const response = await fetch(image_url);
    const arrayBuffer = await response.arrayBuffer();
    const binary: any = new Uint8Array(arrayBuffer);
    const base64String = btoa(String.fromCharCode.apply(null, binary));

    return 'data:image/webp;base64,' + base64String;
  } catch (error) {
    console.log(error);
  }
};

export const groupAndSortBookings = (bookings: any, desc: boolean = true) => {
  return bookings
    ?.reduce(
      (
        groups: { booking_date: string; bookings: IBooking[] }[],
        booking: IBooking,
      ) => {
        const bookingDate = new Date(booking.booking_date).toDateString();
        const existingGroup = groups.find(
          (group: any) => group.booking_date === bookingDate,
        );

        if (existingGroup) {
          existingGroup.bookings = [...existingGroup.bookings, booking]?.sort(
            (a: IBooking, b: IBooking) => {
              const dateA = new Date(a.booking_date);
              const dateB = new Date(b.booking_date);
              return (desc ? -1 : 1) * (dateA.getTime() - dateB.getTime());
            },
          );
        } else {
          groups.push({ booking_date: bookingDate, bookings: [booking] });
        }

        return groups;
      },
      [],
    )
    ?.sort((a: IBooking, b: IBooking) => {
      const dateA = new Date(a.booking_date);
      const dateB = new Date(b.booking_date);
      return (desc ? -1 : 1) * (dateA.getTime() - dateB.getTime());
    });
};

export const scrollToBottom = (ref: any) => {
  ref.current.style.scrollBehavior = 'smooth';
  ref.current.scrollTop = ref.current.scrollHeight;
};

export const calculateScrollPosition = (element: any, setter: any) => {
  const SCROLL_OFFSET = 10;
  const bottom =
    element?.scrollHeight - element?.scrollTop - element?.clientHeight <
    SCROLL_OFFSET;

  if (!bottom) {
    setter(true);
  } else {
    setter(false);
  }
};

export const formatMobileNumber = (mobileNumber: string) => {
  const numericOnly = mobileNumber?.replace(/\D/g, '');

  if (numericOnly?.length === 11) {
    return (
      numericOnly
        .substring(1)
        ?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') || ''
    );
  } else {
    return numericOnly;
  }
};

export const formatPostalCode = (postalCode: string) => {
  if (postalCode?.length === 6) {
    return postalCode?.toUpperCase()?.replace(/(.{3})(.*)/, '$1 $2') || '';
  } else {
    return postalCode?.toUpperCase() || '';
  }
};

export const getBookingStatus = (status: number) => {
  const { label, color } = BOOKING_STATUSES_DISPLAY[status] || {
    label: '',
    color: 'inherit',
  };
  return { label, color };
};

export const calculateNthDay = (day: string) => {
  const nthDay = Number(day);
  if (nthDay >= 11 && nthDay <= 13) {
    return nthDay + 'th';
  }
  switch (nthDay % 10) {
    case 1:
      return nthDay + 'st';
    case 2:
      return nthDay + 'nd';
    case 3:
      return nthDay + 'rd';
    default:
      return nthDay + 'th';
  }
};

export const initStrokes = (image: IImage) => {
  if (!image.strokes) image.strokes = [];
  if (image.strokes.length == 0) image.strokes.push([]);
  if (image.strokes[0].length > 0) image.strokes.unshift([]);
  if (!image.layer) image.layer = 0;
};

export const mergePreserve = (original: any, additional: any) => {
  Object.keys(additional).forEach((key) => {
    original[key] = additional[key];
  });

  if ((original as IImage)?.src) {
    initStrokes(original);
  }

  return original;
};

export const sentenceParts = (
  part: ITranscript,
  index: number,
): ITranscriptSentence[] => {
  const sa: ITranscriptSentence[] = [];

  //const sid = `.${String(!part.timestamp ? index : new Date(part.timestamp).getTime())}`
  const sid = `${index}`;
  if (part?.text) {
    //const sentences = part.text.match(/[^.!?]+[.!?]+/g)
    const sentences = part.text.match(/[^.!?]+(?:[.!?]+|\b(?![^\s]))/g);
    if (sentences)
      sentences.forEach((text, idx) => {
        sa.push({ sid: `${sid}${idx > 0 ? `.${idx}` : ''}`, text });
      });
    else {
      sa.push({ sid, text: part.text });
    }
  }

  return sa;
};

export const renderTranscriptPart: (
  item: ITranscriptPart,
  index?: number,
) => ITranscriptSegment = (item, index = 0) => {
  if ((item as IImage)?.thumb?.src) {
    const image = item as IImage;
    image.ratio = image.width && image.height ? image.width / image.height : 1;

    return {
      image_id: image.id,
      timeStamp: image.date,
      time: !image.date ? 0 : new Date(image.date).getTime(),
    } as ITranscriptSegment;
  } else {
    const part = item as ITranscript;

    return {
      user_id: part.user_id,
      user_name: part.user_name,
      timeStamp: part.timestamp,
      time: !part.timestamp ? 0 : new Date(part.timestamp).getTime(),
      sentences: sentenceParts(part, index),
    } as ITranscriptSegment;
  }
};
/*
export const formatPhoneNumberToE164 = (input: string): string => {
  let digits = input.replace(/\D/g, '');

  digits = digits.substring(0, 11);

  let formattedNumber = digits.replace(
    /^1?(\d{0,3})(\d{0,3})(\d{0,4})/,
    function (_, g1, g2, g3) {
      if (g1.length === 0) return '';
      let part1 = `+1${g1}`;
      if (g1.length < 3) return part1;
      let part2 = `${part1}${g2}`;
      if (g2.length < 3) return part2;
      return `${part2}${g3}`;
    },
  );

  return formattedNumber;
};
*/
export const formatPhoneNumberToE164 = (input: string): string => {
  const digits = input.replace(/\D/g, '');

  // Expecting US/CA numbers: 10 digits, possibly prefixed with a "1"
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  // Invalid or incomplete number — return empty or handle as needed
  return '';
};

export const formatPhoneNumberToNANPFormat = (input: string): string => {
  let digits = input.replace(/\D/g, '');

  digits = digits.substring(0, 11);

  let formattedNumber = digits.replace(
    /^1?(\d{0,3})(\d{0,3})(\d{0,4})/,
    function (_, g1, g2, g3) {
      if (g1.length === 0) return '';
      let part1 = `+1 (${g1}`;
      if (g1.length < 3) return part1;
      let part2 = `${part1}) ${g2}`;
      if (g2.length < 3) return part2;
      return `${part2}-${g3}`;
    },
  );

  return formattedNumber;
};

/*

  The following code to download the report in pdf format will be moved to another file 

import axios from 'axios';
import jsPDF from 'jspdf';
import useCanvasSketch from '../../hooks/useCanvasSketch';
import { SHAPES } from '../../global/constants/general';
import { formatPostalCode } from '../../utils/helpers';

  const {
    penSize,
    activeTab: activeDraw,
    activeShape,
  } = useImageEditorContext();

  const { draw } = useCanvasSketch(
    penSize,
    SHAPES[activeDraw === 'Shape' ? activeShape : activeDraw],
  );
  const addElementsTextToPDF = async (
    doc: any,
    element: Node,
    y: number,
    x: number = 10,
  ): Promise<number> => {
    const pageHeight = doc.internal.pageSize.height;
    if (element.nodeType === Node.TEXT_NODE && element.textContent?.trim()) {
      const text = element.textContent.trim();
      const wrappedText = doc.splitTextToSize(text, 190);
      wrappedText.forEach((line: string) => {
        if (y + lineHeight > pageHeight) {
          doc.addPage();
          y = 10;
        }
        console.log('add line:' + line);
        doc.text(line, x, y);
        y += lineHeight;
      });
    } else if (element.nodeType === Node.ELEMENT_NODE) {
      switch (element.nodeName) {
        case 'H1':
          doc.setFontSize(20);
          break;
        case 'H2':
          doc.setFontSize(18);
          break;
        case 'P':
          doc.setFontSize(12);
          break;
        case 'DIV':
          break;
        case 'IMG':
          console.log('add image here: ', element);

          const imageId = (element as Element)?.getAttribute('title');
          if (!imageId) break;

          const image = imagesMapRef?.current?.get(imageId);
          if (!image) break;

          console.log(`fetch: ${image.src}`, image);

          const response = await axios({
            url: image.src,
            method: 'GET',
            responseType: 'blob',
            headers: {
              Accept: 'image/webp,image/*,* /*;q=0.8',
              'Access-Control-Allow-Origin': '*',
            },
          });

          const blob = new Blob([response.data]);
          const img = new Image();
          const imageUrl = URL.createObjectURL(blob);
          img.src = imageUrl;

          const maxSize = 80;
          const canvasDimensions = await new Promise<{
            width: number;
            height: number;
          }>((resolve, reject) => {
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');

              if (!ctx) {
                reject(new Error('Canvas context could not be initialized'));
                return;
              }

              const canvasWidth =
                maxSize *
                (img.width >= img.height ? 1 : img.width / img.height);
              const canvasHeight =
                maxSize * (img.width < img.height ? 1 : img.height / img.width);

              canvas.width = canvasWidth;
              canvas.height = canvasHeight;

              ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

              if (
                image.layer &&
                image?.strokes.length > 0 &&
                image.strokes[image.layer]
              ) {
                draw(canvas, image.strokes[image.layer]);
              }

              const imgData = canvas.toDataURL('image/jpeg', 1.0);
              doc.addImage(imgData, 'JPEG', 10, y, canvasWidth, canvasHeight);

              URL.revokeObjectURL(imageUrl);
              resolve({ width: canvasWidth, height: canvasHeight });
            };

            img.onerror = (err) => reject(new Error('Failed to load image'));
          });

          //need to move y & x depending if images are adajcent and page with is exceeded
          y += canvasDimensions.height;

          break;
        default:
          doc.setFontSize(12);
      }

      for (const child of Array.from(element.childNodes)) {
        y = await addElementsTextToPDF(doc, child, y, x);
      }
      if (element.nodeName === 'P' || element.nodeName === 'DIV') {
        if (y + lineHeight < pageHeight) {
          y += lineHeight - 7;
        }
      }
      doc.setFontSize(12);
    }
    return y;
  };

  const lineHeight = 10;
  export const generatePDF = async (title: string) => {
    const contentElement = documentRef.current as HTMLElement;
    if (!contentElement) return;

    let y = 10;

    const docTitle = (
      selectedBooking?.payment?.plan.label || 'Untitled Report'
    ).toUpperCase();

    const docSubTitle = selectedBooking?.title || 'Customer';
    const address =
      `${selectedBooking?.address}\n${selectedBooking?.city}${selectedBooking?.city && selectedBooking?.province ? ', ' : ''}${selectedBooking?.province}\n${formatPostalCode(selectedBooking?.postal_code)}` ||
      '';

    const doc = new jsPDF();

    doc.setFillColor(11, 37, 69);

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const rectWidth = pageWidth / 2;
    doc.rect(0, 0, rectWidth, pageHeight, 'F');

    doc.setTextColor(255, 255, 255);

    //title
    doc.setFontSize(26);
    const titleX = 10;
    const titleY = 40;
    doc.text(docTitle + '\nREPORT', titleX, titleY);

    doc.rect(10, 55, pageWidth / 2 - 20, 2, 'F');

    doc.setFontSize(21);
    const homePro = titleY + 40;
    doc.text('Home Pro:\n' + docSubTitle, titleX, homePro);

    //sub
    doc.setFontSize(21);
    const subtitle = homePro + 40;
    doc.text('Submitted To:\n' + docSubTitle, titleX, subtitle);

    //address
    doc.setFontSize(21);
    const addressTitle = subtitle + 40;
    doc.text('Property:\n' + address, titleX, addressTitle);

    doc.setTextColor(0, 0, 0);

    const squares = [
      { x: pageWidth * 0.65, y: 50, size: 35, color: 'blue' },
      { x: pageWidth * 0.8, y: 120, size: 20, color: COLORS.PRIMARY },
      { x: pageWidth * 0.4, y: 150, size: 15, color: 'blue' },
      { x: pageWidth * 0.45, y: 210, size: 50, color: COLORS.PRIMARY },
      { x: pageWidth * 0.7, y: 180, size: 25, color: 'blue' },
      { x: pageWidth * 0.85, y: 250, size: 30, color: COLORS.PRIMARY },
    ];

    squares.forEach((square) => {
      const color: [number, number, number] =
        square.color === 'blue' ? [11, 37, 69] : [255, 165, 0];
      doc.setFillColor(...color);
      doc.rect(square.x, square.y, square.size, square.size, 'F');
    });

    doc.addPage();

    y += lineHeight + 5;
    doc.setFontSize(16);
    const subTitleWidth = doc.getTextWidth(docSubTitle);
    const subCenterX = (doc.internal.pageSize.width - subTitleWidth) / 2;
    doc.text(docSubTitle, subCenterX, y);
    y += lineHeight + 5;

    await addElementsTextToPDF(doc, contentElement, y);

    doc.save(`${title}.pdf`);
  };
*/
export const printDiv = (printContent: string | undefined, title: string) => {
  if (!printContent) return;

  const printWindow = window.open('', '', 'height=600,width=800');
  if (!printWindow) return;

  const styles =
    Array.from(document.head.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((style) => style.outerHTML)
      .join('\n') +
    `
    <style>
      html {
        font-size: 12px;
      }
    </style>
    `;

  printWindow.document.write(`<html><head><title>${title}</title>`);
  printWindow.document.write(styles);
  printWindow.document.write('</head><body>');
  printWindow.document.write(printContent);
  printWindow.document.write('</body></html>');

  printWindow.document
    .querySelectorAll('.button-overlay')
    .forEach((element) => {
      element.remove();
    });

  printWindow.document.close();

  printWindow.onload = () => {
    const body = printWindow.document.body;
    const html = printWindow.document.documentElement;

    body.style.display = 'block';

    const contentWidth = Math.max(body.scrollWidth, html.scrollWidth);
    const contentHeight = Math.max(body.scrollHeight, html.scrollHeight);

    printWindow.resizeTo(
      contentWidth + (printWindow.outerWidth - printWindow.innerWidth),
      contentHeight + (printWindow.outerHeight - printWindow.innerHeight),
    );

    printWindow.print();
    printWindow.close();
  };
};

export const renderOps = (delta: IDelta, defaultElement: string = 'span') => {
  return delta?.ops?.map((op, index) => {
    if (op.insert) {
      const className = op.attributes?.inserted
        ? 'add'
        : op.attributes?.deleted
          ? 'del'
          : null;

      const content =
        typeof op.insert === 'object' ? JSON.stringify(op.insert) : op.insert;

      const props: { key: number; className?: string } = { key: index };
      if (className) {
        props.className = className;
      }

      return React.createElement('span', props, content);
    }
    return null;
  });
};

export const renderOpsToStringWithDelimiters = (
  delta: IDelta,
  addDelimiter: [string, string] = ['<<', '>>'],
  delDelimiter: [string, string] = ['[[', ']]'],
): string => {
  return delta?.ops
    ?.map((op) => {
      if (op.insert) {
        const content =
          typeof op.insert === 'object' ? JSON.stringify(op.insert) : op.insert;

        if (op.attributes?.inserted) {
          return `${addDelimiter[0]}${content}${addDelimiter[1]}`;
        } else if (op.attributes?.deleted) {
          return `${delDelimiter[0]}${content}${delDelimiter[1]}`;
        } else {
          return content;
        }
      }
      return '';
    })
    .join('');
};

export const getFormattedTimezone = (): string => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone; // "America/Toronto"
  return tz;
  /*

  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset(); // in minutes, e.g., 300
  const sign = offsetMinutes > 0 ? '-' : '+';
  const absOffset = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');
  return `GMT${sign}${hours}:${minutes} - ${tz}`;
*/
};

export const applyApplicationFontSize = (fontSize: number) => {
  const html = document.getElementsByTagName('html')[0];
  html.style.fontSize = `${(16 * fontSize) / 100}px`;
};

export const isValidIanaTimezone = (timezone: string) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

export const getOffsetForTimeZone = (date: Date, timeZone: string) => {
  const options = {
    timeZone,
    hour12: false,
    timeZoneName: 'shortOffset' as const,
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);

  const offsetPart = parts.find((p) => p.type === 'timeZoneName');
  return offsetPart?.value.replace('GMT', '') || '+00:00';
};

export const convertCardType = (card: any) => {
  const converted = {
    ...card,
    type:
      card.type === 'Block'
        ? 'TextBlock'
        : card.type === 'Section'
          ? 'Prompt'
          : card.type,
  };

  return converted;
};

const VALID_BLOCK_TYPES = ['div', 'ol', 'ul', 'li', 'h1', 'h2', 'h3'] as const;

const isValidPromptBlock = (obj: any): obj is IPromptBlock => {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.text === 'string' &&
    VALID_BLOCK_TYPES.includes(obj.type) &&
    (obj.ref === undefined ||
      (Array.isArray(obj.ref) &&
        obj.ref.every((r: any) => typeof r === 'string'))) &&
    (obj.children === undefined ||
      (Array.isArray(obj.children) && obj.children.every(isValidPromptBlock)))
  );
};

const collectPromptBlocksRecursively = (
  obj: any,
  accumulator: IPromptBlock[],
) => {
  if (isValidPromptBlock(obj)) {
    const { children, ...rest } = obj;
    accumulator.push(obj);

    if (Array.isArray(children)) {
      for (const child of children) {
        collectPromptBlocksRecursively(child, accumulator);
      }
    }
  }
};

export const extractAndFlattenPromptBlocks = (text: string): IPromptBlock[] => {
  const result: IPromptBlock[] = [];

  const startIdx = text.indexOf('[');
  const endIdx = text.lastIndexOf(']');

  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) {
    return result;
  }

  const jsonSlice = text.slice(startIdx, endIdx + 1);

  try {
    const parsed = JSON.parse(jsonSlice);
    if (Array.isArray(parsed)) {
      for (const block of parsed) {
        collectPromptBlocksRecursively(block, result);
      }
    }
  } catch (err) {
    console.error('Failed to parse JSON:', err);
  }

  return result;
};

export const extractStructuredPromptBlocks = (text: string): IPromptBlock[] => {
  const startIdx = text.indexOf('[');
  const endIdx = text.lastIndexOf(']');

  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) {
    return [];
  }

  const jsonSlice = text.slice(startIdx, endIdx + 1);

  try {
    const parsed = JSON.parse(jsonSlice);
    if (Array.isArray(parsed)) {
      return parsed.filter(isValidPromptBlock);
    }
  } catch (err) {
    console.error('Failed to parse JSON:', err);
  }

  return [];
};

/* sampe data: 
[
  {
    "text": "Request for Sale Information Summary",
    "type": "h1",
    "ref": [".2.0", ".3"],
    "children": []
  },
  {
    "text": "Sale Details",
    "type": "h2",
    "ref": [".6.0", ".8", ".10.0", ".11"],
    "children": [
      {
        "text": "Sale Price: $350,000",
        "type": "li",
        "ref": [".6.0"],
        "children": []
      },
      {
        "text": "Sale Date: May 1, 2025",
        "type": "li",
        "ref": [".10.0", ".11"],
        "children": []
      },
      {
        "text": "Agreement Signed: April 1, 2025",
        "type": "li",
        "ref": [".20.0"],
        "children": []
      }
    ]
*/

//test if this works with partial json, validate that string data is of form [{text: "", type: "div|ol|ul|li|h1|h2|h3", ref, children: […]}, …] - this is strict...
/*export const parseValidPromptBlocks = (input: string): IPromptBlock[] => {
  const results: IPromptBlock[] = [];
  let buffer = '';
  let depth = 0;
  let inObject = false;

  const leadingText = input.split('[')[0].trim();
  const trailingText = input.split(']')[1]?.trim() || '';

  console.log('::::leadingText: ', leadingText);
  console.log('::::trailingText: ', trailingText);

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (!inObject) {
      if (char === '{') {
        inObject = true;
        depth = 1;
        buffer = '{';
      }
    } else {
      buffer += char;

      if (char === '{') {
        depth++;
      } else if (char === '}') {
        depth--;
        if (depth === 0) {
          try {
            const parsed = JSON.parse(buffer);
            if (isValidPromptBlock(parsed)) {
              results.push(parsed);
            } else {
              console.warn('Invalid block structure:', parsed);
            }
          } catch (e) {
            console.error('Failed to parse object:', buffer);
          }
          buffer = '';
          inObject = false;
        }
      }
    }
  }

  if (leadingText) {
    //results.unshift({ type: 'div', text: leadingText });
  }
  if (trailingText) {
    //results.push({ type: 'div', text: trailingText });
  }

  return results;
};
*/
type GeolocationCoords = {
  latitude: number;
  longitude: number;
  altitude: number | null;
  accuracy: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  speed: number | null;
};

type GeolocationPosition = {
  coords: GeolocationCoords;
  timestamp: number;
};

export const extractExifAsGeolocation = (
  file: File,
): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = function () {
      if (!reader.result || typeof reader.result !== 'string') {
        resolve(null);
        return;
      }

      EXIF.getData(reader.result, function (this: any) {
        try {
          const lat = EXIF.getTag(this, 'GPSLatitude');
          const latRef = EXIF.getTag(this, 'GPSLatitudeRef');
          const lon = EXIF.getTag(this, 'GPSLongitude');
          const lonRef = EXIF.getTag(this, 'GPSLongitudeRef');
          const alt = EXIF.getTag(this, 'GPSAltitude');

          if (lat && lon && latRef && lonRef) {
            const toDecimal = (dms: number[], ref: string): number => {
              const [d, m, s] = dms;
              let dec = d + m / 60 + s / 3600;
              return ref === 'S' || ref === 'W' ? -dec : dec;
            };

            const coords: GeolocationCoords = {
              latitude: toDecimal(lat, latRef),
              longitude: toDecimal(lon, lonRef),
              altitude: typeof alt === 'number' ? alt : null,
              accuracy: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null,
            };

            resolve({
              coords,
              timestamp: Date.now(),
            });
          } else {
            resolve(null); // No GPS data found
          }
        } catch {
          resolve(null); // Fallback if any error occurs
        }
      });
    };

    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
};

export const getChunks = (
  transcriptSegments: ITranscriptSegment[],
  minWordCount: number,
) => {
  const chunks: IChunk[] = [];
  let currentChunk = '';
  let currentChunkSids: string[] = [];
  let currentWordCount = 0;
  let currentTimestamp = 0;

  transcriptSegments.forEach((segment) => {
    if (segment.user_name && segment.sentences) {
      currentChunk += `\n${segment.user_name} says: `;

      /*      
      TODO; analyse images and add image note to the transcript,
      TODO; append user notes and corrections (using segment timestamp) to the chunk for the llm
      */

      //currentTimestamp = segment.time; //don't need this if sid is in sentences part
      segment.sentences?.forEach((sentence) => {
        if (!sentence) return;

        const words = sentence.text.trim().split(/\s+/);
        const wordCount = words.length;
        currentWordCount += wordCount;

        //currentChunkSids.push(sentence.sid);//don't need this if sid is in sentences part
        currentChunk += `[${sentence.sid}] ${sentence.text}`;
      });

      if (currentWordCount >= minWordCount) {
        chunks.push({
          sentences: currentChunk,
          sids: currentChunkSids,
          timestamp: currentTimestamp,
        });
        currentChunkSids = [];
        currentChunk = '';
        currentWordCount = 0;
      }
    }
  });

  if (currentChunk) {
    chunks.push({
      sentences: currentChunk,
      sids: currentChunkSids,
      timestamp: currentTimestamp,
    });
  }

  console.log(':::::getChunks:', chunks);
  return chunks;
};

export const getWholeTranscript = (
  transcriptSegments: ITranscriptSegment[],
  transcriptAttachments: Record<string, (IDelta | INote)[]>,
  imagesMapRef: MutableRefObject<Map<string, IImage> | null>,
  structured: boolean = true,
) => {
  let hasImageDescriptions = false;
  const parts: string[] = [];
  transcriptSegments.forEach((segment) => {
    if (segment.user_name && segment.sentences) {
      const name = segment.user_name; //need to replace user_name with homeowner or inspector
      parts.push(
        structured
          ? `${name}: ${segment.sentences?.map((item: ITranscriptSentence) => `[sid:${item.sid}] ${item.text}`).join(' ')}`
          : `${name}: ${segment.sentences?.map((item: ITranscriptSentence) => item.text).join(' ')}`,
      );

      const edits = transcriptAttachments[`edit-${segment.time}`];
      edits?.forEach((item: IDelta | INote) => {
        if ('ops' in item) {
          parts.push(`corrections: "${renderOpsToStringWithDelimiters(item)}"`);
        }
      });

      const notes = transcriptAttachments[`note-${segment.time}`];
      if (notes?.length) {
        notes.forEach((item: IDelta | INote) => {
          if ('title' in item && (item.title || item.description)) {
            parts.push(
              `note: ${item.title ? `${item.title}, ` : ''}${item.description}`,
            );
          }
        });
      }
    } else if (segment.image_id) {
      const image = imagesMapRef.current?.get(segment.image_id);
      if (image?.description?.length) {
        hasImageDescriptions = true;
        const descriptions = image.description
          .filter((desc: any) => desc.response)
          .map((desc: any) => desc.response)
          .join(' ');
        if (descriptions) {
          parts.push(`image[${image.id}] description: ${descriptions}`);
        }

        const notes = image?.date
          ? transcriptAttachments[`note-${new Date(image?.date).getTime()}`]
          : undefined;
        if (notes?.length) {
          notes.forEach((item: IDelta | INote) => {
            if ('title' in item) {
              parts.push(`image note: ${item.title}, ${item.description}`);
            }
          });
        }
      }
    }
  });

  return { parts, hasImageDescriptions };
};

export const deleteAINotes = (
  transcriptAttachments: Record<string, (IDelta | INote)[]>,
) => {
  Object.keys(transcriptAttachments).forEach((key) => {
    const attachments = transcriptAttachments[key] as (IDelta | INote)[];

    if (Array.isArray(attachments)) {
      transcriptAttachments[key] = attachments.filter(
        (item: IDelta | INote) =>
          'src' in item && item.src !== 'a' && item.src !== 'd', //delete AI generated notes
      );

      if (transcriptAttachments[key].length === 0) {
        delete transcriptAttachments[key];
      }
    }
  });
};

export const initialize = (name: string) => {
  return name
    ?.split(' ')
    .slice(0, 2)
    .map((name: string) => name.charAt(0).toUpperCase())
    .join('');
};

export const baseUrl = (url: string) => {
  return !url
    ? ''
    : `${window._env_?.IMAGES_BASE_URL || process.env.REACT_APP_IMAGES_BASE_URL}/${url.replace('/vault/', '')}`;
};

const _deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

export const deepClone = (obj: any) => {
  return (window.structuredClone ?? _deepClone)(obj);
};

export const SIZE_WHITELIST = [
  '8px',
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '26px',
  '28px',
  '30px',
];

export const uuid = () => {
  // or uuidv4()
  return (
    globalThis.crypto?.randomUUID?.().slice(0, 8) ||
    Math.random().toString(36).slice(2) + Date.now()
  ); //crypto.randomUUID().slice(0, 8)
};
